import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import CONSTANTS from "../utils/constants";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "40px",
    borderRadius: "0.375rem",
    borderColor: state.isFocused ? "#F47D39" : "#cccc",
    boxShadow: state.isFocused
      ? "0 0 0 0.25rem rgba(244, 125, 57, 0.25)"
      : null,
    "&:hover": {
      borderColor: "#F47D39",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#F47D39"
      : state.isFocused
      ? "#e9ecef"
      : null,
    color: state.isSelected ? "white" : "black",
  }),
};

const buildHierarchicalList = (items, parentId = null, level = 0) => {
  let list = [];
  const children = items.filter((item) => item.parent_id === parentId);
  children.sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
  for (const item of children) {
    list.push({ ...item, level });
    const childItems = buildHierarchicalList(items, item.id, level + 1);
    list = list.concat(childItems);
  }
  return list;
};

const calculateDiscountPercentage = (originalPrice, discountPrice) => {
  if (
    !originalPrice ||
    !discountPrice ||
    originalPrice <= 0 ||
    discountPrice <= 0
  ) {
    return 0;
  }
  return Math.round(((originalPrice - discountPrice) / originalPrice) * 100);
};

export default function AdminProductsEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [galleryImages, setGalleryImages] = useState([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [newGalleryPreviews, setNewGalleryPreviews] = useState([]);

  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const [productData, setProductData] = useState({
    name_tm: "",
    name_ru: "",
    name_en: "",
    description_tm: "",
    description_ru: "",
    description_en: "",
    price: 0,
    discount_price: null,
    discount_start_date: "",
    discount_end_date: "",
    category_id: "",
    subcategory_id: "",
    brand_id: "",
    barcode: "",
    purchase_limit: null,
    unit_type: 1,
    is_active: true,
  });

  const [variations, setVariations] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [mainCategoryOptions, setMainCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [selectedCategoryJustCategory, setSelectedCategoryJustCategory] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setMessage({ type: "", text: "" });

      try {
        const [categoriesRes, brandsRes] = await Promise.all([
          axios.get(`${CONSTANTS.baseRoute}/categories`).catch((err) => {
            console.error("Categories fetch error:", err);
            return { data: [] };
          }),
          axios.get(`${CONSTANTS.baseRoute}/brands`).catch((err) => {
            console.error("Brands fetch error:", err);
            return { data: [] };
          }),
        ]);

        const allCats = categoriesRes.data;
        setAllCategories(allCats);

        // Ana kategorileri oluştur (parent_id'si olmayanlar)
        const mainCategories = allCats.filter((cat) => !cat.parent_id);
        setMainCategoryOptions(
          mainCategories.map((cat) => ({
            value: cat.id,
            label: cat.name_tm,
            just_category: cat.just_category || false,
          }))
        );

        const allSubCats = allCats.filter((cat) => cat.parent_id);
        setAllSubCategories(allSubCats);

        const hierarchicalBrands = buildHierarchicalList(brandsRes.data, null);
        setBrandOptions(
          hierarchicalBrands.map((brand) => ({
            value: brand.id,
            label: "\u00A0".repeat(brand.level * 4) + brand.name_tm,
          }))
        );

        const productRes = await axios.get(
          `${CONSTANTS.baseRoute}/products/${id}`
        );
        const fetchedProduct = productRes.data;
        setGalleryImages(fetchedProduct.gallery_images || []);

        const formatDateTimeLocal = (dateString) => {
          if (!dateString) return "";
          const date = new Date(dateString);
          date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
          return date.toISOString().slice(0, 16);
        };

        setProductData({
          ...fetchedProduct,
          price: fetchedProduct.price || 0,
          discount_price: fetchedProduct.discount_price || null,
          purchase_limit: fetchedProduct.purchase_limit || null,
          unit_type: fetchedProduct.unit_type || 1,
          discount_start_date: formatDateTimeLocal(
            fetchedProduct.discount_start_date
          ),
          discount_end_date: formatDateTimeLocal(
            fetchedProduct.discount_end_date
          ),
          category_id: fetchedProduct.category_id || "",
          subcategory_id: fetchedProduct.subcategory_id || "",
        });
        setExistingImageUrl(fetchedProduct.image_path);

        const calculatedDiscount = calculateDiscountPercentage(
          fetchedProduct.price || 0,
          fetchedProduct.discount_price || null
        );
        setDiscountPercentage(calculatedDiscount);

        // Kategori seçimlerini ayarla
        if (fetchedProduct.category_id) {
          const category = allCats.find(
            (cat) => cat.id === fetchedProduct.category_id
          );

          if (category) {
            const isJustCategory = category.just_category || false;
            setSelectedCategoryJustCategory(isJustCategory);

            setSelectedMainCategory({
              value: category.id,
              label: category.name_tm,
              just_category: isJustCategory,
            });

            // Eğer kategori just_category değilse alt kategorileri yükle
            if (!isJustCategory) {
              const subs = allCats
                .filter((cat) => cat.parent_id === category.id && !cat.just_category)
                .sort((a, b) => (a.order_number || 0) - (b.order_number || 0));

              setSubCategoryOptions(
                subs.map((cat) => ({
                  value: cat.id,
                  label: cat.name_tm,
                }))
              );
            }
          }
        }

        setVariations(
          (fetchedProduct.variations || []).map((v) => ({
            id: v.id,
            type: v.type || "clothing",
            size: v.size,
            price: v.price ? v.price.toString() : "",
            stock: v.stock ? v.stock.toString() : "",
          }))
        );
      } catch (error) {
        console.error("Fetch error:", error);
        setMessage({
          type: "danger",
          text:
            error.response?.status === 404
              ? t("adminProductsEdit.productNotFound")
              : t("adminProductsEdit.loadError"),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, t]);

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);

    const newPreviews = files.map((file) => URL.createObjectURL(file));

    setNewGalleryFiles((prev) => [...prev, ...files]);
    setNewGalleryPreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeNewGalleryImage = (index) => {
    const updatedFiles = [...newGalleryFiles];
    const updatedPreviews = [...newGalleryPreviews];

    URL.revokeObjectURL(updatedPreviews[index]);
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);

    setNewGalleryFiles(updatedFiles);
    setNewGalleryPreviews(updatedPreviews);
  };
  
  const removeExistingGalleryImage = (index) => {
    const updatedGallery = [...galleryImages];
    updatedGallery.splice(index, 1);
    setGalleryImages(updatedGallery);
  };

  const updateSubCategories = (mainId) => {
    // Sadece just_category = false olan alt kategorileri getir
    const subs = allSubCategories
      .filter((cat) => cat.parent_id === mainId && !cat.just_category)
      .sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
    
    setSubCategoryOptions(
      subs.map((cat) => ({
        value: cat.id,
        label: cat.name_tm,
      }))
    );
  };

  const handleMainCategoryChange = (selectedOption) => {
    setSelectedMainCategory(selectedOption);
    
    // Seçilen kategorinin just_category durumunu kontrol et
    const isJustCategory = selectedOption ? selectedOption.just_category : false;
    setSelectedCategoryJustCategory(isJustCategory);
    
    setProductData((prev) => ({
      ...prev,
      category_id: selectedOption ? selectedOption.value : "",
      // Eğer kategori just_category ise subcategory_id'yi temizle
      subcategory_id: isJustCategory ? "" : prev.subcategory_id,
    }));

    if (selectedOption && !isJustCategory) {
      // Sadece normal kategoriler için alt kategorileri yükle
      updateSubCategories(selectedOption.value);
    } else {
      // just_category ise veya seçim yoksa alt kategorileri temizle
      setSubCategoryOptions([]);
    }
  };

  const handleSubCategoryChange = (selectedOption) => {
    setProductData((prev) => ({
      ...prev,
      subcategory_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    if (name === "unit_type") {
      finalValue = parseInt(value, 10);
    }

    const updatedProductData = {
      ...productData,
      [name]: finalValue,
    };

    setProductData(updatedProductData);

    if (name === "price" || name === "discount_price") {
      const calculatedDiscount = calculateDiscountPercentage(
        name === "price" ? parseFloat(value) || 0 : updatedProductData.price,
        name === "discount_price"
          ? parseFloat(value) || 0
          : updatedProductData.discount_price
      );
      setDiscountPercentage(calculatedDiscount);
    }
  };

  const handleBrandChange = (selectedOption) => {
    setProductData((prev) => ({
      ...prev,
      brand_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleDelete = async () => {
    if (!window.confirm(t("adminProductsEdit.confirmDelete"))) {
      return;
    }

    setIsDeleting(true);
    setMessage({ type: "", text: "" });

    try {
      await axios.delete(`${CONSTANTS.baseRoute}/products/${id}`);
      setMessage({
        type: "success",
        text: t("adminProductsEdit.deleteSuccess"),
      });
      setTimeout(() => navigate("/admin/home/products"), 1500);
    } catch (error) {
      setMessage({
        type: "danger",
        text:
          error.response?.data?.message || t("adminProductsEdit.deleteError"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const processFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setExistingImageUrl(null);
    } else if (file) {
      setMessage({
        type: "danger",
        text: t("adminProductsAdd.onlyImageFiles"),
      });
    }
  };

  const handleImageChange = (e) => processFile(e.target.files[0]);

  const handleRemoveImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleAreaClick = () => fileInputRef.current?.click();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setMessage({ type: "", text: "" });

    if (!productData.name_tm?.trim()) {
      setMessage({
        type: "danger",
        text: t("adminProductsAdd.nameTmRequired"),
      });
      return;
    }
    if (!productData.barcode?.trim()) {
      setMessage({
        type: "danger",
        text: t("adminProductsAdd.barcodeRequired"),
      });
      return;
    }
    if (!productData.category_id) {
      setMessage({
        type: "danger",
        text: t("adminProductsAdd.mainCategoryRequired"),
      });
      return;
    }

    // Eğer seçilen kategori just_category DEĞİLSE alt kategori zorunlu
    if (!selectedCategoryJustCategory && !productData.subcategory_id) {
      setMessage({
        type: "danger",
        text: t("adminProductsAdd.subCategoryRequired"),
      });
      return;
    }

    const priceVal = parseFloat(productData.price?.toString() || "0");
    if (isNaN(priceVal) || priceVal <= 0) {
      setMessage({ type: "danger", text: t("adminProductsAdd.priceRequired") });
      return;
    }

    const stockVal = parseFloat(productData.purchase_limit?.toString() || "0");
    if (isNaN(stockVal) || stockVal < 0) {
      setMessage({ type: "danger", text: t("adminProductsAdd.stockRequired") });
      return;
    }

    const formData = new FormData();

    if (imageFile) {
      formData.append("images", imageFile); 
    } else if (existingImageUrl) {
      formData.append("existing_image_path", existingImageUrl);
    }

    if (newGalleryFiles && newGalleryFiles.length > 0) {
      newGalleryFiles.forEach((file) => {
        formData.append("images", file);
      });
    }

    formData.append("gallery_images", JSON.stringify(galleryImages));

    for (const key in productData) {
      if (key === "gallery_images" || key === "image_path") continue;

      if (
        (key === "discount_price" &&
          (!productData[key] || parseFloat(productData[key]) <= 0)) ||
        (key === "purchase_limit" &&
          (productData[key] === null || productData[key] === ""))
      ) {
        continue;
      }

      if (productData[key] !== null && productData[key] !== undefined) {
        formData.append(key, productData[key]);
      }
    }

    if (variations && variations.length > 0) {
      const validVariations = variations.filter(
        (v) => v.size && parseFloat(v.price) > 0
      );
      if (validVariations.length > 0) {
        formData.append("variations", JSON.stringify(validVariations));
      }
    }

    try {
      await axios.put(`${CONSTANTS.baseRoute}/products/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setMessage({
        type: "success",
        text: t("adminProductsEdit.updateSuccess"),
      });

      setTimeout(() => navigate("/admin/home/products"), 1500);
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      setMessage({
        type: "danger",
        text:
          error.response?.data?.message || t("adminProductsEdit.updateError"),
      });
    }
  };

  if (loading) {
    return (
      <div className="admin-main p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("common.loading")}</span>
        </div>
        <p className="mt-2">{t("adminProductsAdd.loadingData")}</p>
      </div>
    );
  }

  return (
    <div className="admin-main ps-2 pe-3 py-2">
      <div className="d-flex justify-content-between position-sticky top-0 bg-white z-3 mb-3">
        <div className="d-flex gap-2 align-items-center my-2">
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn btn-light" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h5 className="fw-bold mb-0">{t("adminProductsEdit.title")}</h5>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-danger"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {t("common.deleting")}
              </>
            ) : (
              <>
                <i className="bi bi-trash"></i> {t("common.delete")}
              </>
            )}
          </button>

          <button
            type="button"
            className="btn btn-outline-primary shadow-sm d-flex gap-2"
            onClick={() =>
              formRef.current?.dispatchEvent(
                new Event("submit", { bubbles: true, cancelable: true })
              )
            }
          >
            <i className="bi bi-floppy-fill"></i>
            {t("common.save")}
          </button>
        </div>
      </div>

      {message.text && (
        <div
          className={`alert alert-${message.type} mt-4 animate__animated animate__fadeIn`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="needs-validation"
        noValidate
      >
        <div className="row g-4">
          {/* Sol sütun - Resim ve Kategori */}
          <div className="col-lg-4">
            <div className="card shadow-sm mb-4 border-0">
              <div className="card-header bg-light border-bottom">
                <h5 className="mb-0 text-dark">
                  {t("adminProductsAdd.productImage")}
                </h5>
              </div>
              <div className="card-body">
                {/* --- ANA RESİM BÖLÜMÜ --- */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-secondary small uppercase">
                    {t("adminProductsAdd.mainImage") || "Esasy surat"}
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {imagePreview || existingImageUrl ? (
                    <div
                      className="position-relative border rounded overflow-hidden bg-light"
                      style={{ height: "200px" }}
                    >
                      <img
                        src={
                          imagePreview ||
                          `${CONSTANTS.baseRoute}${existingImageUrl}`
                        }
                        alt="Main"
                        className="w-100 h-100"
                        style={{ objectFit: "contain" }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 m-2 shadow"
                        onClick={handleRemoveImage}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={handleAreaClick}
                      className="border-dashed d-flex flex-column align-items-center justify-content-center bg-light rounded cursor-pointer"
                      style={{ height: "200px" }}
                    >
                      <i className="bi bi-image fs-1 text-muted"></i>
                      <p className="mb-0 small text-primary fw-bold">
                        {t("adminProductsAdd.clickToSelectMain") ||
                          "Esasy surat saýla"}
                      </p>
                    </div>
                  )}
                </div>

                {/* --- GALERİ RESİMLERİ BÖLÜMÜ --- */}
                <div className="mt-4">
                  <label className="form-label fw-bold text-secondary small uppercase">
                    {t("adminProductsAdd.gallery") || "Galeriýa (Maks. 10)"}
                  </label>

                  <div className="row g-2">
                    {galleryImages &&
                      galleryImages.map((img, idx) => (
                        <div key={`existing-${idx}`} className="col-4">
                          <div
                            className="position-relative border rounded overflow-hidden bg-light"
                            style={{ height: "80px" }}
                          >
                            <img
                              src={`${CONSTANTS.baseRoute}${img}`}
                              className="w-100 h-100"
                              style={{ objectFit: "contain" }}
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 m-1 rounded-circle"
                              style={{
                                width: "20px",
                                height: "20px",
                                zIndex: 10,
                              }}
                              onClick={() => removeExistingGalleryImage(idx)}
                            >
                              <i
                                className="bi bi-x"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </button>
                          </div>
                        </div>
                      ))}

                    {newGalleryPreviews &&
                      newGalleryPreviews.map((preview, idx) => (
                        <div key={`new-${idx}`} className="col-4">
                          <div
                            className="position-relative border rounded overflow-hidden bg-light"
                            style={{ height: "80px" }}
                          >
                            <img
                              src={preview}
                              className="w-100 h-100"
                              style={{ objectFit: "contain" }}
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 m-1 rounded-circle"
                              style={{ width: "20px", height: "20px" }}
                              onClick={() => removeNewGalleryImage(idx)}
                            >
                              <i
                                className="bi bi-x"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    <div className="col-4">
                      <label
                        className="border-dashed d-flex align-items-center justify-content-center bg-white rounded cursor-pointer w-100"
                        style={{ height: "80px" }}
                      >
                        <input
                          type="file"
                          multiple
                          className="d-none"
                          onChange={handleGalleryChange}
                          accept="image/*"
                        />
                        <i className="bi bi-plus-lg fs-4 text-muted"></i>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0">
              <div className="card-header bg-light border-bottom">
                <h5 className="mb-0 text-dark">
                  {t("adminProductsAdd.categoryAndSub")}
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label
                    htmlFor="category_id"
                    className="form-label fw-semibold"
                  >
                    {t("adminProductsAdd.mainCategory")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={mainCategoryOptions}
                    value={mainCategoryOptions.find(
                      (opt) => opt.value === productData.category_id
                    )}
                    onChange={handleMainCategoryChange}
                    placeholder={t("adminProductsAdd.selectMainCategory")}
                    isClearable
                    isSearchable
                    noOptionsMessage={() => t("common.noResults")}
                    styles={customSelectStyles}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                  />
                </div>

                {/* Alt kategori alanı - Sadece seçilen kategori just_category değilse göster */}
                {!selectedCategoryJustCategory && (
                  <div className="mb-3">
                    <label
                      htmlFor="subcategory_id"
                      className="form-label fw-semibold"
                    >
                      {t("adminProductsAdd.subCategory")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={subCategoryOptions}
                      value={subCategoryOptions.find(
                        (opt) => opt.value === productData.subcategory_id
                      )}
                      onChange={handleSubCategoryChange}
                      placeholder={t("adminProductsAdd.selectSubCategory")}
                      isClearable
                      isSearchable
                      isDisabled={!selectedMainCategory}
                      noOptionsMessage={() =>
                        selectedMainCategory
                          ? t("adminProductsAdd.noSubFound")
                          : t("adminProductsAdd.selectMainFirst")
                      }
                      styles={customSelectStyles}
                    />
                  </div>
                )}

                {/* Seçilen kategori just_category ise bilgi mesajı */}
                {selectedCategoryJustCategory && (
                  <div className="alert alert-info mb-3 py-2">
                    <i className="bi bi-info-circle me-2"></i>
                    {t("adminProductsAdd.selectedCategoryIsJustCategory")}
                  </div>
                )}

                <div className="form-check form-switch p-0">
                  <label
                    className="form-check-label fw-semibold mb-2 me-2"
                    htmlFor="is_active"
                  >
                    {t("adminProductsAdd.productActive")}
                  </label>
                  <input
                    className="form-check-input ms-0"
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={productData.is_active}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sağ sütun - Diğer bilgiler */}
          <div className="col-lg-8">
            <div className="card shadow-sm mb-4 border-0">
              <div className="card-header bg-light border-bottom">
                <h5 className="mb-0 text-dark">
                  {t("adminProductsAdd.mainInfo")}
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="name_tm" className="form-label fw-semibold">
                      {t("adminProductsAdd.nameTm")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name_tm"
                      name="name_tm"
                      value={productData.name_tm}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="name_ru" className="form-label fw-semibold">
                      {t("adminProductsAdd.nameRu")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name_ru"
                      name="name_ru"
                      value={productData.name_ru}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="barcode" className="form-label fw-semibold">
                      {t("adminProductsAdd.barcode")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="barcode"
                      name="barcode"
                      value={productData.barcode}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label
                      htmlFor="purchase_limit"
                      className="form-label fw-semibold"
                    >
                      {t("adminProductsAdd.stockQuantity")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        id="purchase_limit"
                        name="purchase_limit"
                        value={productData.purchase_limit || ""}
                        onChange={handleChange}
                        min="0"
                        step="0.001"
                        required
                      />
                      <select
                        className="form-select"
                        id="unit_type"
                        name="unit_type"
                        value={productData.unit_type}
                        onChange={handleChange}
                        style={{ flex: "0 0 auto", width: "auto" }}
                      >
                        <option value="1">{t("units.piece")}</option>
                        <option value="2">{t("units.kg")}</option>
                        <option value="3">{t("units.gram")}</option>
                        <option value="4">{t("units.liter")}</option>
                        <option value="5">{t("units.meter")}</option>
                        <option value="6">{t("units.package")}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-light border-bottom">
                <h5 className="mb-0 text-dark">
                  {t("adminProductsAdd.priceAndDiscount")}
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="price" className="form-label fw-semibold">
                      {t("adminProductsAdd.regularPrice")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id="price"
                        name="price"
                        value={productData.price}
                        onChange={handleChange}
                        min="0"
                        required
                      />
                      <span className="input-group-text">TMT</span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label
                      htmlFor="discount_price"
                      className="form-label fw-semibold"
                    >
                      {t("adminProductsAdd.discountPrice")}
                    </label>
                    <div className="input-group">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id="discount_price"
                        name="discount_price"
                        value={productData.discount_price || ""}
                        onChange={handleChange}
                        min="0"
                      />
                      <span className="input-group-text">TMT</span>
                    </div>
                    {discountPercentage > 0 && (
                      <div className="mt-2">
                        <span className="badge bg-success">
                          {discountPercentage}% {t("adminProductsAdd.discount")}
                        </span>
                        <small className="text-muted ms-2">
                          {t("adminProductsAdd.originalPrice")}:{" "}
                          {productData.price} TMT
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label
                      htmlFor="discount_start_date"
                      className="form-label fw-semibold"
                    >
                      {t("adminProductsAdd.discountStart")}
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="discount_start_date"
                      name="discount_start_date"
                      value={productData.discount_start_date}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label
                      htmlFor="discount_end_date"
                      className="form-label fw-semibold"
                    >
                      {t("adminProductsAdd.discountEnd")}
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="discount_end_date"
                      name="discount_end_date"
                      value={productData.discount_end_date}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow border-0">
              <div className="card-header">
                <h5>{t("adminProductsAdd.detailedDescription")}</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t("adminProductsAdd.descriptionTm")}
                  </label>
                  <textarea
                    className="form-control"
                    rows="4"
                    name="description_tm"
                    value={productData.description_tm}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t("adminProductsAdd.descriptionRu")}
                  </label>
                  <textarea
                    className="form-control"
                    rows="4"
                    name="description_ru"
                    value={productData.description_ru}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}